import { useState, type ReactNode } from "react";
import { Modal } from "../Modal";

/**
 * G-9.2 kit: the one confirm/reason dialog that replaces every native
 * prompt / confirm dialog in the SPA (FINALIZING.md D-11 — on iOS the
 * "block dialogs" setting can suppress native dialogs entirely, which would
 * strand a courier unable to report a failure).
 *
 * `reasonMode`:
 *  - "none"     → plain confirm (message + confirm/cancel)
 *  - "optional" → textarea shown, may be left empty
 *  - "required" → textarea shown, confirm blocked until `minReasonLength`
 */
export function ReasonModal({
  title,
  message,
  reasonMode = "none",
  reasonLabel = "Reason",
  reasonPlaceholder,
  minReasonLength = 3,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  error = null,
  children,
  onConfirm,
  onClose,
}: {
  title: string;
  message?: string;
  reasonMode?: "none" | "optional" | "required";
  reasonLabel?: string;
  reasonPlaceholder?: string;
  minReasonLength?: number;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  /** A failed confirm, shown inside the dialog so it never fails silently. */
  error?: string | null;
  /** Extra controls between the message and the reason (e.g. a photo input). */
  children?: ReactNode;
  onConfirm: (reason: string | undefined) => void | Promise<void>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  const trimmed = reason.trim();
  const reasonInvalid =
    reasonMode === "required" && trimmed.length < minReasonLength;

  const confirm = () => {
    setTouched(true);
    if (reasonInvalid) return;
    void onConfirm(trimmed.length > 0 ? trimmed : undefined);
  };

  return (
    <Modal title={title} onClose={onClose}>
      {message ? <p className="reason-modal-message">{message}</p> : null}
      {error ? (
        <div className="alert error" role="alert">
          {error}
        </div>
      ) : null}
      {children}
      {reasonMode !== "none" ? (
        <div className="field full">
          <label htmlFor="reason-modal-input">
            {reasonLabel}
            {reasonMode === "optional" ? " (optional)" : ""}
          </label>
          <textarea
            id="reason-modal-input"
            rows={3}
            value={reason}
            placeholder={reasonPlaceholder}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={busy}
          />
          {touched && reasonInvalid ? (
            <small className="reason-modal-error" role="alert">
              Please give at least {minReasonLength} characters.
            </small>
          ) : null}
        </div>
      ) : null}
      <div className="button-row reason-modal-actions">
        <button
          type="button"
          className={destructive ? "button danger" : "button"}
          onClick={confirm}
          disabled={busy || (touched && reasonInvalid)}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
        <button type="button" className="button plain" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </button>
      </div>
    </Modal>
  );
}
